import { DataFlow } from 'app/model/dataflow/dataflow';
import { Transformation } from 'app/model/dataflow/transformation';
import { Owned } from 'app/model/base/named';
import { Metamodel } from 'app/model/base/model';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DeriveTransformation } from 'app/model/dataflow/derivetransformation';
import { JoinTransformation } from 'app/model/dataflow/jointransformation';
import { ExistsTransformation } from 'app/model/dataflow/existstransformation';
import { SelectTransformation } from '../../dataflow/selectTransformation';
import { FilterTransformation } from '../../dataflow/filtertransformation';
import { AggregateTransformation } from '../../dataflow/aggregatetransformation';
import { SortTransformation } from '../../dataflow/sorttransformation';
import { ConditionalSplitTransformation } from 'app/model/dataflow/conditionalsplittransformation';
import { UnionTransformation } from '../../dataflow/uniontransformation';
import { LookupTransformation } from '../../dataflow/lookuptransformation';
import { SurrogateKeyTransformation } from '../../dataflow/surrogatekeytransformation';
import { ExtendTransformation } from '../../dataflow/extendtransformation';
import { PivotTransformation } from 'app/model/dataflow/pivottransformation';
import { UnpivotTransformation } from 'app/model/dataflow/unpivottransformation';
import { WindowTransformation } from 'app/model/dataflow/windowtransformation';
import { AlterRowTransformation } from 'app/model/dataflow/alterrowtransformation';
import { IScriptProperties } from './dataflow.adapter';
import { DataFlowParameter } from 'app/model/dataflow/dataflow-parameter';
import { DataFlowFunction } from 'app/model/dataflow/dataflow-function';
import { TransformPlainObject } from 'worker/app-workers/shared/plainObjects/transform.plainObject';
import { SourcePlainObject } from 'worker/app-workers/shared/plainObjects/source.plainObject';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { ColumnMappingBaseTransformPlainObject } from 'worker/app-workers/shared/plainObjects/columnMappingBase.plainObject';
import { SourceConvertor } from './adapterVisitor/convertors/source.convertor';
import { SinkConvertor } from './adapterVisitor/convertors/sink.convertor';
import { TransformConvertorBase } from './adapterVisitor/convertors/transform.convertor';
import { SelectConvertor } from './adapterVisitor/convertors/select.convertor';
import { DerivePlainObject } from 'worker/app-workers/shared/plainObjects/derive.plainObject';
import { DeriveConvertor } from './adapterVisitor/convertors/derive.convertor';
import { AggregateConvertor } from './adapterVisitor/convertors/aggregate.convertor';
import { WindowConvertor } from './adapterVisitor/convertors/window.convertor';
import { PivotConvertor } from './adapterVisitor/convertors/pivot.convertor';
import { UnpivotConvertor } from './adapterVisitor/convertors/unpivot.convertor';
import { SurrogateKeyConvertor } from './adapterVisitor/convertors/surrogateKey.convertor';
import { JoinConvertor } from './adapterVisitor/convertors/join.convertor';
import { ExistsConvertor } from './adapterVisitor/convertors/exists.convertor';
import { UnionConvertor } from './adapterVisitor/convertors/union.convertor';
import { ConditionalSplitConvertor } from './adapterVisitor/convertors/conditionalSplit.convertor';
import { FilterConvertor } from './adapterVisitor/convertors/filter.convertor';
import { SortConvertor } from './adapterVisitor/convertors/sort.convertor';
import { AlterRowConvertor } from './adapterVisitor/convertors/alterRow.convertor';
import { ExtendConvertor } from './adapterVisitor/convertors/extend.convertor';
import { LookupConvertor } from './adapterVisitor/convertors/lookup.convertor';
import { FunctionConvertor } from './adapterVisitor/convertors/function.convertor';
import { ParameterConvertor } from './adapterVisitor/convertors/parameter.convertor';
import { DataFlowPOParserFactory } from 'app/dataflow/dataFlowPO.parser.factory';
import { DATAFLOW_PARSING_STRATEGY } from 'app/dataflow/dataflowPO.parser.models';
import { SinkPlainObject } from 'worker/app-workers/shared/plainObjects/sink.plainObject';
import { FoldDownTransformation } from 'app/model/dataflow/folddowntransformation';
import { FoldDownConvertor } from './adapterVisitor/convertors/folddown.convertor';
import { RankTransformation } from 'app/model/dataflow/ranktransformation';
import { RankConvertor } from './adapterVisitor/convertors/rank.convertor';
import { ParseTransformation } from 'app/model/dataflow/parsetransformation';
import { ParseConvertor } from './adapterVisitor/convertors/parse.convertor';
import { ExecuteFlowletConvertor } from './adapterVisitor/convertors/executeFlowlet.convertor';
import { AssertTransformation } from 'app/model/dataflow/asserttransformation';
import { AssertConvertor } from './adapterVisitor/convertors/assert.convertor';
import { InputTransformation } from 'app/model/dataflow/inputtransformation';
import { OutputTransformation } from 'app/model/dataflow/outputtransformation';
import { InputConvertor } from './adapterVisitor/convertors/input.convertor';
import { OutputConvertor } from './adapterVisitor/convertors/output.convertor';
import { InputPlainObject } from 'worker/app-workers/shared/plainObjects/input.plainObject';
import { OutputPlainObject } from 'worker/app-workers/shared/plainObjects/output.plainObject';
import { StringifyTransformation } from 'app/model/dataflow/stringifytransformation';
import { StringifyConvertor } from './adapterVisitor/convertors/stringify.converter';
import { GuardTransformation } from 'app/model/dataflow/guardtransformation';
import { GuardConvertor } from './adapterVisitor/convertors/guard.convertor';
import { CallTransformation } from 'app/model/dataflow/calltransformation';
import { CallConvertor } from './adapterVisitor/convertors/call.convertor';
import { CastTransformation } from 'app/model/dataflow/casttransformation';
import { CastConvertor } from './adapterVisitor/convertors/cast.convertor';
import { IDataFlowPOParserWorkerOptions } from 'app/dataflow/IDataFlowPOParser';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { IResourceContext } from 'shell-models-lib';

export enum ParseScriptMethod {
    ALL,
    PARAMETERS,
    TRANSFORMATIONS
}

export interface IDataFlowModelParserOption extends IDataFlowPOParserWorkerOptions {
    method: ParseScriptMethod;
}

export class DataFlowParser {
    // Adding here to avoid circular dependency with execute flowlet -> flowlet -> data flow parser
    private static _EXECUTE_FLOWLET_MODEL_NAME = 'compose';
    private static _CALL_TRANFORM_MODEL_NAME = 'call';

    public async parseDataFlowScriptAsync(
        contextData: IResourceContext,
        flowScript: string,
        dataflow: DataFlow,
        nullableOption?: Partial<IDataFlowModelParserOption>
    ): Promise<IScriptProperties> {
        const nonNullableOption: Partial<IDataFlowModelParserOption> = {
            method: ParseScriptMethod.ALL,
            fastQueue: false,
            ...(nullableOption ?? {})
        };

        const scriptProperties: IScriptProperties = {
            functions: new Set<DataFlowFunction>(),
            parameters: new Set<DataFlowParameter>(),
            transformations: new Set<Transformation>()
        };

        const parsingStrategy = await getServiceInstance(contextData, DATAFLOW_PARSING_STRATEGY);
        const plainObjectParser = DataFlowPOParserFactory.createInstance(parsingStrategy);
        const plainObjects = await plainObjectParser.parseDSL(contextData, flowScript, dataflow.name, nonNullableOption);

        if (
            nonNullableOption.method === ParseScriptMethod.ALL ||
            nonNullableOption.method === ParseScriptMethod.PARAMETERS
        ) {
            for (const funcPlainObject of plainObjects.functions) {
                const convertor = new FunctionConvertor(funcPlainObject, dataflow);
                const func = await convertor.convert();
                scriptProperties.functions.add(func);
            }

            for (const paramPlainObject of plainObjects.parameters) {
                const convertor = new ParameterConvertor(paramPlainObject, dataflow);
                const param = await convertor.convert();
                scriptProperties.parameters.add(param);
            }

            for (const transformPlainObject of plainObjects.transformations.filter(
                (transformPlainObject) =>
                    transformPlainObject.modelName === DataFlowParser._EXECUTE_FLOWLET_MODEL_NAME ||
                    transformPlainObject.modelName === DataFlowParser._CALL_TRANFORM_MODEL_NAME
            )) {
                const convertor = this._getTransformationConvertor(transformPlainObject, dataflow);
                const transform = await convertor.convert(contextData);
                scriptProperties.transformations.add(transform);
            }
        }

        if (
            nonNullableOption.method === ParseScriptMethod.ALL ||
            nonNullableOption.method === ParseScriptMethod.TRANSFORMATIONS
        ) {
            for (const transformPlainObject of plainObjects.transformations.filter(
                (transformPlainObject) => transformPlainObject.modelName !== DataFlowParser._EXECUTE_FLOWLET_MODEL_NAME
            )) {
                if (transformPlainObject.modelName === DataFlowParser._CALL_TRANFORM_MODEL_NAME) {
                    // Due to how call transform is persisted, it might already be in the dataflow so don't add it again
                    const txName = transformPlainObject.name;
                    if (dataflow.transformations.find((transform) => transform.name === txName)) {
                        continue;
                    }
                }
                const convertor = this._getTransformationConvertor(transformPlainObject, dataflow);
                const transform = await convertor.convert(contextData);
                scriptProperties.transformations.add(transform);
            }
        }

        return scriptProperties;
    }

    public async parseSinkScriptAsync(
        contextData: IResourceContext,
        sinkScript: string,
        sink: DataFlowSink
    ): Promise<void> {
        const parsingStrategy = await getServiceInstance(contextData, DATAFLOW_PARSING_STRATEGY);
        const plainObjectParser = DataFlowPOParserFactory.createInstance(parsingStrategy);
        const plainObjects = await plainObjectParser.parseDSL(contextData, sinkScript, '');

        await Promise.all(
            plainObjects.transformations.map(async (transformPlainObject) => {
                const convertor = new SinkConvertor(transformPlainObject as SinkPlainObject, sink);
                await convertor.convert(contextData);
            })
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected _getTransformationConvertor(
        transformationObject: TransformPlainObject,
        dataflow: DataFlow
    ): TransformConvertorBase {
        let transformation: Transformation;
        let transformName = transformationObject.modelName;

        if (transformName === 'filter') {
            transformName = FilterTransformation.MODEL.name;
        } else if (transformName === 'lookup') {
            transformName = LookupTransformation.MODEL.name;
        }

        if (transformName === 'source') {
            transformation = dataflow.sources.find((source) => source.name === transformationObject.name);
        } else if (transformName === 'sink') {
            transformation = dataflow.sinks.find((sink) => sink.name === transformationObject.name);
        } else {
            const transformationMetamodel = Metamodel.modelByName(transformName);
            transformation = Owned.create<Transformation>(transformationMetamodel, dataflow);
        }

        switch (transformName) {
            case DataFlowSource.MODEL.name:
                return new SourceConvertor(transformationObject as SourcePlainObject, transformation as DataFlowSource);
            case DataFlowSink.MODEL.name:
                return new SinkConvertor(transformationObject as SinkPlainObject, transformation as DataFlowSink);
            case SelectTransformation.MODEL.name:
                return new SelectConvertor(
                    transformationObject as ColumnMappingBaseTransformPlainObject,
                    transformation
                );
            case DeriveTransformation.MODEL.name:
                return new DeriveConvertor(transformationObject as DerivePlainObject, transformation);
            case AggregateTransformation.MODEL.name:
                return new AggregateConvertor(transformationObject, transformation);
            case WindowTransformation.MODEL.name:
                return new WindowConvertor(transformationObject, transformation);
            case PivotTransformation.MODEL.name:
                return new PivotConvertor(transformationObject, transformation);
            case UnpivotTransformation.MODEL.name:
                return new UnpivotConvertor(transformationObject, transformation);
            case SurrogateKeyTransformation.MODEL.name:
                return new SurrogateKeyConvertor(transformationObject, transformation);
            case JoinTransformation.MODEL.name:
                return new JoinConvertor(transformationObject, transformation);
            case ExistsTransformation.MODEL.name:
                return new ExistsConvertor(transformationObject, transformation);
            case UnionTransformation.MODEL.name:
                return new UnionConvertor(transformationObject, transformation);
            case ConditionalSplitTransformation.MODEL.name:
                return new ConditionalSplitConvertor(transformationObject, transformation);
            case FilterTransformation.MODEL.name:
                return new FilterConvertor(transformationObject, transformation);
            case SortTransformation.MODEL.name:
                return new SortConvertor(transformationObject, transformation);
            case RankTransformation.MODEL.name:
                return new RankConvertor(transformationObject, transformation);
            case ParseTransformation.MODEL.name:
                return new ParseConvertor(transformationObject, transformation);
            case AlterRowTransformation.MODEL.name:
                return new AlterRowConvertor(transformationObject, transformation);
            case ExtendTransformation.MODEL.name:
                return new ExtendConvertor(transformationObject, transformation);
            case LookupTransformation.MODEL.name:
                return new LookupConvertor(transformationObject, transformation);
            case FoldDownTransformation.MODEL.name:
                return new FoldDownConvertor(transformationObject, transformation);
            case DataFlowParser._EXECUTE_FLOWLET_MODEL_NAME:
                return new ExecuteFlowletConvertor(
                    transformationObject as ColumnMappingBaseTransformPlainObject,
                    transformation
                );
            case InputTransformation.MODEL.name:
                return new InputConvertor(
                    transformationObject as InputPlainObject,
                    transformation as InputTransformation
                );
            case OutputTransformation.MODEL.name:
                return new OutputConvertor(
                    transformationObject as OutputPlainObject,
                    transformation as OutputTransformation
                );
            case AssertTransformation.MODEL.name:
                return new AssertConvertor(transformationObject, transformation);
            case CallTransformation.MODEL.name:
                return new CallConvertor(transformationObject, transformation);
            case StringifyTransformation.MODEL.name:
                return new StringifyConvertor(transformationObject, transformation);
            case GuardTransformation.MODEL.name:
                return new GuardConvertor(transformationObject, transformation);
            case CastTransformation.MODEL.name:
                return new CastConvertor(transformationObject, transformation);
            default:
                return undefined;
        }
    }
}
